import { PkButton, PkIcon, PkChip } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { useEffect, useState, Fragment, lazy, useMemo, Suspense } from 'react';

import { FiltersStoreProvider, useContent, useFilters } from './store';

import { useWindowSize } from '@hooks/useWindowSize';
import Loader from '@shared/Loader';
import { useTranslateFn } from '@shared/Translate';
import { loadFromAPIs } from '@utilities/fetch';
import { uefaBaseUrl } from '@utilities/WindowContext';

import './styles.scss';

const getFiltersComponent = (name: string) => lazy(() => import(/* webpackPrefetch: true */ `./filters/${name}Filter`));

export interface Competition {
  displayName?: string;
  id: string;
}
export type ComposableFilterProps = {
  title: string;
  competitions: Competition[];
  sections: string[];
  language: string;
  baseLibraryUrl: string;
  filters: Record<string, any>;
  compApiUrl: string;
  apiKey: string;
  competitionUrl: string;
  componentItem: string;
  placeholder: string;
  seasons: string[];
  chambers: string[];
  useQueryString: boolean;
};
const baseClass = 'composable-filters';

const ComposableFilter = ({ options }: { options: ComposableFilterProps }) => {
  const t = useTranslateFn();
  const [filters, setFilters] = useFilters();
  const [savedFilters, setSavedFilters] = useState(options.filters);
  const [, setContent] = useContent();
  const [expanded, setExpanded] = useState(false);
  const { isMobile } = useWindowSize({ minTabletWidth: 601 });

  const expandedFilters = useMemo(() => {
    return [
      ...Object.keys(options.filters).map((key) => {
        const FilterComp = getFiltersComponent(key);
        return (
          <Suspense key={`filter_${key}`}>
            {FilterComp && (
              <div className={`${baseClass}__filter ${baseClass}__filter--${key} `}>
                <FilterComp options={options} />
              </div>
            )}
          </Suspense>
        );
      }),
    ].filter(Boolean);
  }, [options]);

  const activeFilters = useMemo(
    () => Object.entries(savedFilters).filter(([, f]) => f?.value && f.value !== 'all'),
    [savedFilters],
  );

  const selectedFilters = useMemo(
    () => Object.entries(filters).filter(([, f]) => f?.value && f.value !== 'all'),
    [filters],
  );

  const clearFilter = (key: string) => () => {
    setSavedFilters((prevValue) => ({
      ...prevValue,
      [key]: options.filters[key],
    }));
    setFilters({
      ...filters,
      [key]: options.filters[key],
    });
  };

  const clearAll = () => {
    setSavedFilters(options.filters);
    setFilters(options.filters);
  };

  const applyFilters = () => {
    if (selectedFilters.length) setSavedFilters(filters);
  };

  const toggleExpanded = () => {
    setExpanded((prevState) => !prevState);
  };

  useEffect(() => {
    const composableFilterEl = document.getElementsByClassName('composable-filters')[0] as HTMLElement;

    const filteredContentEl = document.getElementsByClassName('content-margin')[0] as HTMLElement;

    if (expanded) {
      if (filteredContentEl) filteredContentEl.classList.add('content-margin--expanded');
      composableFilterEl.classList.add('background-class');
    } else {
      if (filteredContentEl) filteredContentEl.classList.remove('content-margin--expanded');
      composableFilterEl.classList.remove('background-class');
    }
  }, [expanded]);

  useEffect(() => {
    setExpanded(false);
    setContent('');

    const orderedFilters: Record<string, any> = Object.keys(savedFilters)
      .toSorted((a, b) => b.localeCompare(a))
      .reduce((obj, key) => {
        obj[key] = savedFilters[key];
        return obj;
      }, {});

    const library = options.useQueryString
      ? Object.keys(orderedFilters).reduce((lib, f, i) => {
          return savedFilters[f]?.value ? `${lib}${i === 0 ? '?' : '&'}${f}=${savedFilters[f].value}` : lib;
        }, `${options.baseLibraryUrl}/_content`)
      : `${Object.values(orderedFilters)
          .filter((f) => f?.value)
          .reduce((lib, f) => `${lib}/${f.value}`, options.baseLibraryUrl)}`;

    loadFromAPIs(
      `${library}${options.useQueryString ? '' : '/_content'}`,
      {},
      { responseType: 'text', apiUrl: uefaBaseUrl },
    ).then((newContent) => {
      setContent(newContent);
    });
  }, [savedFilters, options.baseLibraryUrl, setContent, options.useQueryString]);

  return (
    <>
      <div
        className={clsx(
          '${baseClass}__wrap',
          expanded && (isMobile ? `pk-pb--l` : 'pk-pb--xl2'),
          'pk-container pk-bg--elevation-01',
        )}
      >
        <div className={`${baseClass}__header pk-col pk-col--span-12`}>
          {!!options.title && <div className={`${baseClass}__title`}>{t(options.title)}</div>}

          {isMobile && (
            <PkButton appearance='icon' onPkClick={toggleExpanded}>
              <PkIcon name='actions-filter' />
            </PkButton>
          )}
          {!isMobile && (
            <PkButton appearance='outlined' onPkClick={toggleExpanded} width='160'>
              {t('filter')}
              {activeFilters.length > 0 && (
                <span className={`${baseClass}__active-filters`}> ({activeFilters.length})</span>
              )}
            </PkButton>
          )}
        </div>
        {!expanded && activeFilters.length > 0 && (
          <div className={`${baseClass}__applied-filters pk-col pk-col--span-12`}>
            {activeFilters.length > 1 && (
              <PkButton className='pk-ml--0' appearance='text' type='button' onPkClick={clearAll}>
                {t('clear_all')}
              </PkButton>
            )}

            {activeFilters.map(([key, filter]) => (
              <Fragment key={`filter_clear_${key}`}>
                <div className={`${baseClass}__selected-filter`}>
                  <PkChip className='chip' type='filter' onPkClick={clearFilter(key)}>
                    {t(filter.label)}
                  </PkChip>
                </div>
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div className='pk-container'>
          <div className={`${baseClass}__expanded-filters pk-col pk-col--span-12`}>
            {expandedFilters}

            <div className={`${baseClass}__buttons-wrap`}>
              <PkButton
                appearance='contained'
                className={isMobile ? 'pk-mr--xs' : 'pk-mr--l'}
                onPkClick={applyFilters}
                disabled={!selectedFilters.length ? true : undefined}
                width={!isMobile && '160'}
              >
                {t('apply')}
              </PkButton>
              <PkButton
                appearance='outlined'
                onPkClick={clearAll}
                disabled={!selectedFilters.length && !activeFilters.length ? true : undefined}
                width={!isMobile && '160'}
              >
                {t('clear')}
              </PkButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Content = () => {
  const [content] = useContent();
  return (
    <div className={`${baseClass}__filtered-content-wrapper`}>
      {content ? <div className={`${baseClass}__filtered-content`}>{parseHtmlToReact(content ?? '')}</div> : <Loader />}
    </div>
  );
};

const App = ({ options }) => (
  <FiltersStoreProvider>
    <ComposableFilter options={options} />
    <Content />
  </FiltersStoreProvider>
);

export default App;
